import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Sparkles, Info } from "lucide-react";

const sizes = [4, 6, 8, 10, 12, 15, 20, 25, 30];

const PRONOVO_BASE = 350;
const PRONOVO_PER_KWP_TO_30 = 400;
const PRONOVO_PER_KWP_OVER_30 = 300;

const PLZ_TO_CANTON = [
  [1000, 1299, "VD"], [1200, 1299, "GE"], [1300, 1399, "VD"],
  [1400, 1599, "VD"], [1600, 1699, "FR"], [1700, 1799, "FR"],
  [1800, 1899, "VD"], [1900, 1999, "VS"], [2000, 2099, "NE"],
  [2300, 2399, "NE"], [2400, 2499, "NE"], [2500, 2599, "BE"],
  [2600, 2999, "JU"], [3000, 3999, "BE"], [3900, 3999, "VS"],
  [4000, 4099, "BS"], [4100, 4199, "BL"], [4200, 4299, "BL"],
  [4300, 4499, "AG"], [4500, 4599, "SO"], [4600, 4799, "SO"],
  [4800, 4899, "AG"], [4900, 4999, "BL"], [5000, 5999, "AG"],
  [6000, 6099, "LU"], [6100, 6299, "LU"], [6300, 6399, "ZG"],
  [6400, 6499, "SZ"], [6500, 6599, "TI"], [6600, 6999, "TI"],
  [7000, 7599, "GR"], [7600, 7999, "GR"], [8000, 8099, "ZH"],
  [8100, 8499, "ZH"], [8500, 8599, "TG"], [8600, 8999, "ZH"],
  [9000, 9099, "SG"], [9100, 9199, "AR"], [9200, 9299, "TG"],
  [9300, 9399, "SG"], [9400, 9499, "SG"], [9500, 9599, "TG"],
  [9600, 9699, "SG"], [9700, 9999, "SG"],
];

const CANTON_NAMES = {
  ZH: "Zürich", BE: "Bern", LU: "Luzern", UR: "Uri", SZ: "Schwyz",
  OW: "Obwalden", NW: "Nidwalden", GL: "Glarus", ZG: "Zug",
  FR: "Freiburg", SO: "Solothurn", BS: "Basel-Stadt", BL: "Basel-Landschaft",
  SH: "Schaffhausen", AR: "Appenzell A.Rh.", AI: "Appenzell I.Rh.",
  SG: "St. Gallen", GR: "Graubünden", AG: "Aargau", TG: "Thurgau",
  TI: "Tessin", VD: "Waadt", VS: "Wallis", NE: "Neuenburg",
  GE: "Genf", JU: "Jura",
};

const KANTON_BONUS = {
  ZH: { pv_chf_per_kwp: 0, battery_bonus_chf: 0, note: "Keine kantonale PV-Förderung; einzelne Gemeinden bieten Beiträge." },
  BE: { pv_chf_per_kwp: 100, battery_bonus_chf: 0, note: "Kantonaler PV-Bonus (Richtwert)." },
  LU: { pv_chf_per_kwp: 0, battery_bonus_chf: 0, note: "Förderung über Gemeinden möglich." },
  ZG: { pv_chf_per_kwp: 200, battery_bonus_chf: 1500, note: "Zusätzlicher Beitrag pro kWp und Speicher-Bonus." },
  SZ: { pv_chf_per_kwp: 100, battery_bonus_chf: 1000, note: "Förderbeitrag inkl. Speicherbonus." },
  SG: { pv_chf_per_kwp: 150, battery_bonus_chf: 1000, note: "Kantonale Beiträge zusätzlich zum Bund." },
  TG: { pv_chf_per_kwp: 200, battery_bonus_chf: 1500, note: "Thurgauer Energieförderprogramm – inkl. Speicherbonus." },
  AR: { pv_chf_per_kwp: 100, battery_bonus_chf: 500, note: "Förderprogramm Appenzell A.Rh." },
  AI: { pv_chf_per_kwp: 100, battery_bonus_chf: 500, note: "Förderprogramm Appenzell I.Rh." },
  GR: { pv_chf_per_kwp: 150, battery_bonus_chf: 1000, note: "Bündner Förderprogramm." },
  AG: { pv_chf_per_kwp: 0, battery_bonus_chf: 0, note: "Förderung primär über Gemeinden." },
  SO: { pv_chf_per_kwp: 100, battery_bonus_chf: 0, note: "Solothurner Förderprogramm." },
  BL: { pv_chf_per_kwp: 200, battery_bonus_chf: 1500, note: "Basel-Landschaft Energieförderung." },
  BS: { pv_chf_per_kwp: 300, battery_bonus_chf: 2000, note: "Basel-Stadt – attraktives Förderprogramm." },
  GL: { pv_chf_per_kwp: 100, battery_bonus_chf: 500, note: "Glarner Energieförderprogramm." },
  SH: { pv_chf_per_kwp: 100, battery_bonus_chf: 500, note: "Schaffhauser Förderprogramm." },
  NW: { pv_chf_per_kwp: 100, battery_bonus_chf: 500, note: "Nidwaldner Förderprogramm." },
  OW: { pv_chf_per_kwp: 100, battery_bonus_chf: 500, note: "Obwaldner Förderprogramm." },
  UR: { pv_chf_per_kwp: 100, battery_bonus_chf: 500, note: "Urner Förderprogramm." },
  FR: { pv_chf_per_kwp: 100, battery_bonus_chf: 500, note: "Freiburger Förderprogramm." },
  JU: { pv_chf_per_kwp: 100, battery_bonus_chf: 500, note: "Jurassisches Förderprogramm." },
  NE: { pv_chf_per_kwp: 100, battery_bonus_chf: 500, note: "Neuenburger Förderprogramm." },
  VD: { pv_chf_per_kwp: 100, battery_bonus_chf: 500, note: "Waadtländer Förderprogramm." },
  VS: { pv_chf_per_kwp: 100, battery_bonus_chf: 500, note: "Walliser Förderprogramm." },
  GE: { pv_chf_per_kwp: 200, battery_bonus_chf: 1500, note: "Genfer Förderprogramm." },
  TI: { pv_chf_per_kwp: 100, battery_bonus_chf: 500, note: "Tessiner Förderprogramm." },
};

const plzToCanton = (plz) => {
  const value = Number(plz);
  if (!Number.isInteger(value) || value < 1000 || value > 9999) return null;
  return PLZ_TO_CANTON.find(([start, end]) => start <= value && value <= end)?.[2] || null;
};

const pronovoEinmalverguetung = (kwp) => {
  if (kwp <= 0) return 0;
  if (kwp <= 30) return Math.round(PRONOVO_BASE + PRONOVO_PER_KWP_TO_30 * kwp);
  const base30 = PRONOVO_BASE + PRONOVO_PER_KWP_TO_30 * 30;
  return Math.round(base30 + PRONOVO_PER_KWP_OVER_30 * (kwp - 30));
};

const calculateFoerderung = ({ plz, kwp, withBattery }) => {
  const cantonCode = plzToCanton(plz);
  const cantonName = CANTON_NAMES[cantonCode] || null;
  const bund = pronovoEinmalverguetung(kwp);
  const cantonData = KANTON_BONUS[cantonCode] || {
    pv_chf_per_kwp: 0,
    battery_bonus_chf: 0,
    note: "Für diese PLZ liegen uns aktuell keine kantonalen Richtwerte vor.",
  };
  const kantonPv = Math.round(cantonData.pv_chf_per_kwp * kwp);
  const kantonSpeicher = withBattery ? cantonData.battery_bonus_chf : 0;
  const kantonTotal = kantonPv + kantonSpeicher;

  return {
    plz,
    kwp,
    with_battery: withBattery,
    canton: { code: cantonCode, name: cantonName },
    bund: {
      label: "Pronovo Einmalvergütung (KLEIV)",
      amount_chf: bund,
    },
    kanton: {
      code: cantonCode,
      name: cantonName,
      pv_amount_chf: kantonPv,
      battery_amount_chf: kantonSpeicher,
      total_chf: kantonTotal,
      note: cantonData.note,
    },
    total_chf: bund + kantonTotal,
    disclaimer:
      "Richtwerte Stand 2026. Tatsächliche Förderbeiträge variieren nach Gemeinde, Anlagenkonfiguration und Verfügbarkeit der Programme. Verbindliche Auskunft erhalten Sie bei Pronovo, Ihrem Kanton und Ihrer Gemeinde.",
  };
};

export default function FoerderCheck() {
  const [plz, setPlz] = useState("");
  const [kwp, setKwp] = useState(10);
  const [withBattery, setWithBattery] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const calc = async (e) => {
    e?.preventDefault?.();
    if (!/^\d{4}$/.test(plz)) {
      toast.error("Bitte gültige 4-stellige Schweizer PLZ eingeben.");
      return;
    }
    setLoading(true);
    try {
      const data = calculateFoerderung({
        plz,
        kwp,
        withBattery,
      });
      setResult(data);
    } catch (err) {
      console.error(err);
      toast.error("Förder-Check fehlgeschlagen. Bitte erneut versuchen.");
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n) =>
    typeof n === "number" ? `CHF ${n.toLocaleString("de-CH")}` : "—";

  return (
    <section
      id="foerder-check"
      data-testid="foerder-section"
      className="section relative bg-[var(--sol-bg)] border-t hairline"
    >
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-3xl mx-auto">
          <p className="eyebrow">Förder-Check</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extralight tracking-tight mt-6 text-white leading-[1.05]">
            Wie viel Förderung
            <br />
            <span className="bronze-metallic">erhalte ich?</span>
          </h2>
          <p className="font-body text-white/55 mt-6 text-base md:text-lg leading-relaxed">
            PLZ und Anlagengrösse eingeben – sofort sehen, welche Beiträge
            Bund und Kanton beisteuern.
          </p>
        </div>

        <form
          onSubmit={calc}
          data-testid="foerder-form"
          className="mt-12 bg-[var(--sol-surface)] border border-white/10 rounded-3xl p-6 md:p-10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]"
        >
          <div className="grid md:grid-cols-3 gap-6 md:gap-10">
            <div>
              <label className="text-[11px] tracking-[0.22em] uppercase text-white/40">
                PLZ
              </label>
              <input
                data-testid="foerder-input-plz"
                inputMode="numeric"
                maxLength={4}
                value={plz}
                onChange={(e) => setPlz(e.target.value.replace(/\D/g, "").slice(0, 4))}
                placeholder="9225"
                className="sol-input"
              />
            </div>
            <div>
              <label className="text-[11px] tracking-[0.22em] uppercase text-white/40">
                Anlagengrösse: <span className="text-white">{kwp} kWp</span>
              </label>
              <input
                type="range"
                data-testid="foerder-input-kwp"
                min={4}
                max={30}
                step={1}
                value={kwp}
                onChange={(e) => setKwp(parseInt(e.target.value, 10))}
                className="w-full mt-4 accent-[var(--sol-bronze)]"
              />
              <div className="flex justify-between text-[10px] text-white/35 mt-1 font-body">
                <span>4 kWp</span>
                <span>30 kWp</span>
              </div>
            </div>
            <div className="flex flex-col">
              <label className="text-[11px] tracking-[0.22em] uppercase text-white/40">
                Batteriespeicher
              </label>
              <label className="mt-4 inline-flex items-center gap-3 cursor-pointer select-none">
                <span
                  data-testid="foerder-input-battery"
                  onClick={() => setWithBattery((v) => !v)}
                  className={`relative inline-block w-12 h-7 rounded-full transition-colors ${
                    withBattery
                      ? "bg-[var(--sol-bronze)]"
                      : "bg-white/15"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full transition-transform ${
                      withBattery ? "translate-x-5" : ""
                    }`}
                  />
                </span>
                <span className="text-white/70 text-sm font-body">
                  {withBattery ? "Ja, mit Speicher" : "Ohne Speicher"}
                </span>
              </label>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-2">
            {sizes.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setKwp(s)}
                data-testid={`foerder-quick-${s}`}
                className={`text-xs px-3.5 py-1.5 rounded-full border transition ${
                  kwp === s
                    ? "border-[var(--sol-bronze)] bg-[var(--sol-bronze)]/15 text-white"
                    : "border-white/10 text-white/55 hover:border-white/30 hover:text-white"
                }`}
              >
                {s} kWp
              </button>
            ))}
          </div>

          <div className="mt-8 flex items-center justify-end">
            <button
              type="submit"
              disabled={loading}
              data-testid="foerder-submit"
              className="btn-bronze rounded-full px-8 py-4 inline-flex items-center gap-2 text-sm font-medium disabled:opacity-60"
            >
              {loading ? "Berechne…" : "Förderung berechnen"}
              {!loading && <ArrowRight size={16} />}
            </button>
          </div>
        </form>

        {result && (
          <div
            data-testid="foerder-result"
            className="mt-8 bg-gradient-to-b from-[var(--sol-surface)] to-[var(--sol-bg)] border border-[var(--sol-bronze)]/30 rounded-3xl p-6 md:p-10"
          >
            <div className="flex items-center gap-3">
              <Sparkles size={18} className="text-[var(--sol-bronze-light)]" />
              <p className="eyebrow">
                Ihre geschätzten Förderbeiträge
                {result.canton?.name && ` · ${result.canton.name}`}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-4 md:gap-6 mt-8">
              <div className="bg-[var(--sol-surface-2)] border border-white/8 rounded-2xl p-6">
                <p className="text-[10px] tracking-[0.22em] uppercase text-white/40 font-body">
                  Bund
                </p>
                <p className="font-display text-2xl md:text-3xl mt-3 text-white font-light">
                  {fmt(result.bund.amount_chf)}
                </p>
                <p className="text-[11px] text-white/45 mt-2 font-body">
                  {result.bund.label}
                </p>
              </div>
              <div className="bg-[var(--sol-surface-2)] border border-white/8 rounded-2xl p-6">
                <p className="text-[10px] tracking-[0.22em] uppercase text-white/40 font-body">
                  Kanton {result.canton?.code || "—"}
                </p>
                <p className="font-display text-2xl md:text-3xl mt-3 text-white font-light">
                  {fmt(result.kanton.total_chf)}
                </p>
                <p className="text-[11px] text-white/45 mt-2 font-body">
                  PV: {fmt(result.kanton.pv_amount_chf)}
                  {result.kanton.battery_amount_chf > 0 &&
                    ` + Speicher: ${fmt(result.kanton.battery_amount_chf)}`}
                </p>
              </div>
              <div className="bg-[var(--sol-bronze)]/10 border border-[var(--sol-bronze)]/40 rounded-2xl p-6">
                <p className="text-[10px] tracking-[0.22em] uppercase text-[var(--sol-bronze-light)] font-body">
                  Total geschätzt
                </p>
                <p className="font-display text-2xl md:text-3xl mt-3 bronze-metallic font-light">
                  {fmt(result.total_chf)}
                </p>
                <p className="text-[11px] text-white/45 mt-2 font-body">
                  Einmalige Beiträge
                </p>
              </div>
            </div>

            {result.kanton?.note && (
              <p className="text-sm text-white/55 mt-6 font-body">
                <span className="text-[var(--sol-bronze)]">Hinweis:</span>{" "}
                {result.kanton.note}
              </p>
            )}

            <div className="mt-6 flex items-start gap-3 text-xs text-white/40 font-body">
              <Info size={14} className="mt-0.5 shrink-0" />
              <p>{result.disclaimer}</p>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href="#solar-check"
                data-testid="foerder-cta-solar-check"
                className="btn-bronze rounded-full px-7 py-3.5 inline-flex items-center justify-center gap-2 text-sm font-medium"
              >
                Solar-Check starten
                <ArrowRight size={16} />
              </a>
              <a
                href="#kontakt"
                data-testid="foerder-cta-kontakt"
                className="btn-ghost rounded-full px-7 py-3.5 inline-flex items-center justify-center text-sm font-medium"
              >
                Persönliche Offerte anfordern
              </a>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
